import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Row } from "antd";
import styled from "styled-components";
import { BranchesOutlined, DownloadOutlined } from "@ant-design/icons";
import { getDateChangeTime, exportExcel } from "../../../../../helper/helper";
import Title from "../../../../components/Title";
import FilterTime from "../../../../components/FilterTime";
import { TYPE_GET_DATE } from "../../../../../helper/const";
import { DualAxes } from "@ant-design/plots";

export default function Overview({ handleGetData, tabActive, isMissCall = false }) {
  const [startDate, setStartDate] = useState(
    getDateChangeTime(TYPE_GET_DATE.oneMonthAgo)
  );
  const [endDate, setEndDate] = useState(getDateChangeTime(TYPE_GET_DATE.now));

  const { result = {} } =
      useSelector((state) => state.reducerNatcom?.data) || {};
    const { data: airtime } = result;

  useEffect(() => {
    if (tabActive !== "1") return;

    handleGetData(startDate, endDate);
  }, [startDate, endDate, tabActive, handleGetData]);

  const { columnData, lineData } = useMemo(() => {
    if (!airtime || typeof airtime !== 'object') return { columnData: [], lineData: [] };
    
    const columnResult = [];
    const lineResult = [];
    
    Object.keys(airtime).forEach(date => {
      const dayData = airtime[date];
      if (!Array.isArray(dayData)) return;
      
      const totals = dayData.reduce(
        (acc, hour) => ({
          total: acc.total + (hour.total || 0),
          check_sub_error: acc.check_sub_error + (hour.check_sub_error || 0),
          not_enough_money: acc.not_enough_money + (hour.not_enough_money || 0),
          cancel: acc.cancel + (hour.cancel || 0),
          call_invalid: acc.call_invalid + (hour.call_invalid || 0),
        }),
        { total: 0, check_sub_error: 0, not_enough_money: 0, cancel: 0, call_invalid: 0 }
      );
      
      columnResult.push({
        date: date,
        total: totals.total,
        type: "Total",
      });

        const calcPercent = (value) => {
          return totals.total > 0 ? Number(((value / totals.total) * 100).toFixed(2)) : 0;
        };      

      const formatNumber = (num) =>
        num.toLocaleString("vi-VN", { maximumFractionDigits: 0 });        
      
      lineResult.push(
        {
          date: date,
          value: totals.check_sub_error,
          percent: calcPercent(totals.check_sub_error),
          type: "Check Sub Error",
        },
        {
          date: date,
          value: totals.not_enough_money,
          percent: calcPercent(totals.not_enough_money),
          type: "Not Enough Money",
        },
        {
          date: date,
          value: totals.cancel,
          percent: calcPercent(totals.cancel),
          type: "Cancel",
        },
        {
          date: date,
          value: totals.call_invalid,
          percent: calcPercent(totals.call_invalid),
          type: "Call Invalid",
        }
      );
    });
    
    // Sort by date
    columnResult.sort((a, b) => new Date(a.date) - new Date(b.date));
    lineResult.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return { columnData: columnResult, lineData: lineResult };
  }, [airtime]);

  const handleExportExcel = () => {
    if (!airtime || typeof airtime !== 'object') {
      console.log('No data available to export');
      return;
    }

    const exportData = {};
    
    Object.keys(airtime).forEach(date => {
      const dayData = airtime[date];
      if (!Array.isArray(dayData)) return;
      
      // Sum all values for the day
      const totals = dayData.reduce(
        (acc, hour) => ({
          total: acc.total + (hour.total || 0),
          check_sub_error: acc.check_sub_error + (hour.check_sub_error || 0),
          not_enough_money: acc.not_enough_money + (hour.not_enough_money || 0),
          cancel: acc.cancel + (hour.cancel || 0),
          call_invalid: acc.call_invalid + (hour.call_invalid || 0),
        }),
        { total: 0, check_sub_error: 0, not_enough_money: 0, cancel: 0, call_invalid: 0 }
      );
      
      exportData[date] = [
        {
          date: date,
          total: totals.total,
          check_sub_error: totals.check_sub_error,
          not_enough_money: totals.not_enough_money,
          cancel: totals.cancel,
          call_invalid: totals.call_invalid,
        }
      ];
    });

    exportExcel(exportData, "miss_call_errors");
  };

  return (
    <>
      <div
        className="position-relative d-flex justify-right mb-2 gap-2"
        style={{ justifyContent: "right" }}
      >
        <FilterTime
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <div className="d-flex justify-content-end">
          <Button 
            onClick={handleExportExcel} 
            className="d-flex align-items-center"
          >
            <DownloadOutlined /> 
            Export Excel
          </Button>
        </div>
      </div>
      <Container className="panel panel-sortable">
        <Title titleName="Overview">
          <BranchesOutlined />
        </Title>
        <Col span={24}>
          <Col xs={24} lg={24} className="container-item">
            <div className="item-chart">
              <h4 className="pt-3 pe-3 pb-">Overview</h4>
              {columnData.length > 0 ? (
                <DualAxes
                  data={[columnData, lineData]}
                  xField="date"
                  yField={["total", "value"]}
                  geometryOptions={[
                    {
                      geometry: "column",
                      color: "#5B8FF9",
                    },
                    {
                      geometry: "line",
                      seriesField: "type",
                      color: ["#2FC3F2", "#30BF78", "#FAAD14", "#8543E0"],
                      lineStyle: {
                        lineWidth: 2,
                      },
                      point: {
                        size: 3,
                        shape: "circle",
                      },
                    },
                  ]}
                  tooltip={{
                    shared: true,
                    showCrosshairs: true,
                    formatter: (item) => {
                      if (!item.type) { // columnData thường không có type
                        return {
                          name: "Total",
                          value: item.total?.toLocaleString("vi-VN")
                        };
                      }

                      const totalItem = columnData.find(c => c.date === item.date);
                      const percent = totalItem ? ((item.value / totalItem.total) * 100).toFixed(2) : 0;

                      return {
                        name: item.type,
                        value: `${item.value?.toLocaleString("vi-VN")} (${percent}%)`
                      };
                    }
                  }}
                  meta={{
                    total: {
                      alias: "Total",
                      formatter: (v) => v?.toLocaleString(),
                    },
                    value: {
                      alias: "Error Count",
                      formatter: (v) => v?.toLocaleString(),
                    },
                     percent: {
                      formatter: (v) => `${v}%`, // thêm khai báo percent để tooltip nhìn thấy
                    },
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  No data available
                </div>
              )}
            </div>
          </Col>
        </Col>
      </Container>
    </>
  );
}

const Container = styled(Row)``;
